import React, { useCallback, useEffect, useRef, useState } from "react";
import { fileOpen } from "browser-fs-access";
import { MenuProps, Dropdown, Modal, Input, Flex } from 'antd';
import {
    exportToSvg,
    restoreElements,
    loadFromBlob,
    loadLibraryFromBlob,
} from "../../common";
import type { BinaryFiles } from "../../utils/types";
import type { SchemadrawElement } from "../../element/types";
import {
    CalculatorOutlined, GroupOutlined, LinkOutlined, FileDoneOutlined, ControlOutlined,
    FileImageOutlined, StepForwardOutlined, ReloadOutlined, PauseCircleOutlined, CaretRightOutlined, MenuOutlined
} from '@ant-design/icons';
import { getBeginTimeList } from "../../utils/animate";
import { exportToSvgFile, exportToWebmFile, prepareWebmData } from "../../common/export";
import { getNonDeletedElements } from "../../element";

const linkRegex =
    /#json=([a-zA-Z0-9_-]+),?([a-zA-Z0-9_-]*)/;

const getCombinedBeginTimeList = (svgList: Props["svgList"]) => {
    const beginTimeList = ([] as number[]).concat(
        ...svgList.map(({ svg }) =>
            getBeginTimeList(svg).map((n) => Math.floor(n / 100) * 100)
        )
    );
    return [...new Set(beginTimeList)].sort((a, b) => a - b);
};

type Props = {
    svgList: {
        svg: SVGSVGElement;
        finishedMs: number;
    }[];
    loadDataList: (
        data: {
            elements: readonly SchemadrawElement[];
            appState: Parameters<typeof exportToSvg>[0]["appState"];
            files: BinaryFiles;
        }[]
    ) => void;
};

const Toolbar: React.FC<Props> = ({ svgList, loadDataList }) => {
    const [showToolbar, setShowToolbar] = useState<boolean>(false);
    const [paused, setPaused] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [link, setLink] = useState("");
    const [webmData, setWebmData] = useState<Blob>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    useEffect(() => {
        setWebmData(undefined);
    }, [svgList]);

    useEffect(() => {
        svgList.forEach(({ svg }) => {
            if (paused) {
                svg.pauseAnimations();
            } else {
                svg.unpauseAnimations();
            }
        });
    }, [svgList, paused]);

    useEffect(() => {
        const hash = window.location.hash.slice(1);
        const searchParams = new URLSearchParams(hash);
        if (searchParams.get("toolbar") !== "no") {
            setShowToolbar(true);
        } else {
            setShowToolbar(false);
        }
    }, []);

    const loadFile = async () => {
        const blob = await fileOpen({
            description: "Schemadraw files",
            extensions: [".schemadraw"],
            mimeTypes: ["application/json"],
        });

        let data = await loadFromBlob(blob, null, null);
        loadDataList([data]);
    };

    const loadLibrary = async () => {
        const blob = await fileOpen({
            description: "Schemadraw library files",
            extensions: [".schemadrawlib"],
            mimeTypes: ["application/json"],
        });
        const libraryItems = await loadLibraryFromBlob(blob);
        const dataList = libraryItems.map((libraryItem) =>
            getNonDeletedElements(restoreElements(libraryItem.elements, null))
        );
        loadDataList(
            dataList.map((elements) => ({ elements, appState: {}, files: {} }))
        );
    };

    const loadLink = () => {
        const match = linkRegex.exec(link);
        if (!match) {
            window.alert("Invalid link");
            return;
        }
        if (match[1]) {
            window.location.hash = match[0];
        }
        window.location.reload();
    };

    const togglePausedAnimations = useCallback(() => {
        if (!svgList.length) {
            return;
        }
        setPaused((p) => !p);
    }, [svgList]);

    const timer = useRef<NodeJS.Timeout>();
    const stepForwardAnimations = useCallback(() => {
        if (!svgList.length) {
            return;
        }
        const beginTimeList = getCombinedBeginTimeList(svgList);
        const currentTime = svgList[0].svg.getCurrentTime() * 1000;
        let nextTime = beginTimeList.find((t) => t > currentTime + 50);
        if (nextTime) {
            nextTime -= 1;
        } else {
            nextTime = currentTime + 500;
        }
        clearTimeout(timer.current as NodeJS.Timeout);
        svgList.forEach(({ svg }) => {
            svg.unpauseAnimations();
        });
        timer.current = setTimeout(() => {
            svgList.forEach(({ svg }) => {
                svg.pauseAnimations();
                svg.setCurrentTime((nextTime as number) / 1000);
            });
            setPaused(true);
        }, nextTime - currentTime);
    }, [svgList]);

    const resetAnimations = useCallback(() => {
        svgList.forEach(({ svg }) => {
            svg.setCurrentTime(0);
        });
    }, [svgList]);

    useEffect(() => {
        const onKeydown = (e: KeyboardEvent) => {
            if (e.key.toLowerCase() === "p") {
                togglePausedAnimations();
            } else if (e.key.toLowerCase() === "f") {
                stepForwardAnimations();
            } else if (e.key.toLowerCase() === "r") {
                resetAnimations();
            } else if (e.key.toLowerCase() === "h") {
                // hide toolbar
                setShowToolbar(false);
            } else if (e.key.toLowerCase() === "s") {
                // show toolbar otherwise
                setShowToolbar(true);
            }
        };
        document.addEventListener("keydown", onKeydown);
        return () => {
            document.removeEventListener("keydown", onKeydown);
        };
    }, [togglePausedAnimations, stepForwardAnimations, resetAnimations]);

    const changeToolbarStatus = () => {
        setShowToolbar(!showToolbar);
    };

    const showModal = () => {
        setIsModalOpen(true);
    };

    const exportToSvg = () => {
        if (!svgList.length) {
            return;
        }
        svgList.forEach(({ svg }) => {
            exportToSvgFile(svg);
        });
    };

    const exportToWebm = async () => {
        let toolbarStatus = showToolbar;
        if (!svgList.length) {
            return;
        }
        if (webmData) {
            await exportToWebmFile(webmData);
            return;
        }
        setProcessing(true);
        setShowToolbar(false);
        try {
            const data = await prepareWebmData(svgList);
            setWebmData(data);
        } catch (e) {
            console.log(e);
        }
        setShowToolbar(toolbarStatus);
        setProcessing(false);
    };

    const items: MenuProps['items'] = [
        {
            key: '1',
            type: 'group',
            label: '加载数据',
            children: [
                {
                    label: <span onClick={loadFile}><CalculatorOutlined rev={undefined} />&nbsp;本地设计</span>,
                    key: '1-1',
                }, {
                    label: <span onClick={loadLibrary}><GroupOutlined rev={undefined} />&nbsp;本地素材</span>,
                    key: '1-2',
                }, {
                    label: <span onClick={showModal}><LinkOutlined rev={undefined} />&nbsp;共享链接</span>,
                    key: '1-3',
                },],
        },
        {
            key: '2',
            type: 'group',
            label: '导出数据',
            children: [
                {
                    label: <span onClick={exportToSvg}><FileDoneOutlined rev={undefined} />&nbsp;导出为SVG</span>,
                    key: '2-1',
                }, {
                    label: <div onClick={exportToWebm}><FileImageOutlined rev={undefined} />&nbsp;
                        {processing
                            ? "处理中..."
                            : webmData
                                ? "导出 WebM"
                                : "准备 WebM"}
                    </div>,
                    key: '2-2',
                    disabled: processing,
                },
            ]
        },
        {
            key: '3',
            type: 'group',
            label: '工具条',
            children: [
                {
                    label: <span onClick={changeToolbarStatus}><ControlOutlined rev={undefined} />&nbsp;
                        {!showToolbar ? '显示(s)' : '隐藏(h)'}
                    </span>,
                    key: '3-1',
                }
            ],
        }
    ];
    let menu = (
        <Dropdown menu={{ items }}>
            <MenuOutlined rev={undefined} />
        </Dropdown>
    );

    return (
        <Flex gap="middle" vertical={false} className="toolbar">
            <div className="tool-menu">
                {menu}
            </div>
            <Flex gap="middle" vertical={true} className="tool-list" style={{ display: showToolbar ? 'flex' : 'none' }}>
                <div className="tool-item" onClick={togglePausedAnimations} title={paused ? "开始(p)" : "暂停(p)"}>
                    {paused ? <CaretRightOutlined rev={undefined} /> : <PauseCircleOutlined rev={undefined} />}
                </div>
                <div className="tool-item" onClick={stepForwardAnimations} title='单步(f)'>
                    <StepForwardOutlined rev={undefined} />
                </div>
                <div className="tool-item" onClick={resetAnimations} title='重置(r)'>
                    <ReloadOutlined rev={undefined} />
                </div>
            </Flex>
            <Modal title="Basic Modal" open={isModalOpen} onOk={loadLink} onCancel={() => setIsModalOpen(false)}>
                <div style={{ display: linkRegex.test(link) ? 'none' : 'block' }}></div>
                <Input placeholder="输入链接..."
                    value={link}
                    onChange={(e) => setLink(e.target.value)} />
            </Modal>

        </Flex>
    );
};

export default Toolbar;